<template>
  <div class="container-fluid">

    <div v-if="isApplicationFormVisible">
      <PublicAdmissionForm 
        :key="admission_form_id"
        @logout="logout" 
        @formSubmitted="handleFormSubmission" 
        :email="email"
        :admission_form_id="admission_form_id" 
      />
    </div>

    <div v-else class="card login-card shadow border-white p-5 m-5 bg-white">
      <b-col class="text-center mb-5">

        <strong v-if="admissionFormDetails.institution.name" class="d-block text-primary fs-1 fw-bold">
          {{ admissionFormDetails.institution.name }}
        </strong>
        <strong v-if="admissionFormDetails.name" class="d-block text-primary fs-3 fw-semibold">
          {{ admissionFormDetails.name }}
        </strong>
        <strong class="d-block mt-3 text-dark fs-5 fw-medium border-top pt-2">
          Student Admission Form
        </strong>
      </b-col>

      <b-col>

        <!-- Email Input -->
        <b-form-group>
          <label class="required-label">Email:</label>
          <b-form-input 
            id="email-input" 
            type="email" 
            v-model="email" 
            :disabled="otpResendCountdown > 0"
            placeholder="Enter Email" 
          />
        </b-form-group>

        <div class="text-center">
          <span v-if="!errorMsg && name" class="text-success"><b>{{ name }}</b></span>
        </div>

        <b-form-group v-if="errorMsg">
          <label class="required-label">Name:</label>
          <b-form-input 
            id="name-input" 
            v-model="name" 
            :disabled="otpResendCountdown > 0" 
            placeholder="Enter Name" 
          />
        </b-form-group>

        <!-- OTP Section -->
        <b-row class="justify-content-center">
          <span v-if="loginStatus === 'sending'">Sending OTP...</span>
          <div v-else>
            <b-button 
              v-if="otpResendCountdown === 0" 
              :disabled="!email || !isValidEmail(email)" 
              class="btn-success" 
              @click="sendOtp"
            >
              Get OTP
            </b-button>
            <span v-if="otpResendCountdown > 0" class="text-info">
              You can resend OTP after {{ otpResendCountdown }} seconds
            </span>
          </div>
        </b-row>

        <div v-if="otp || loginStatus === 'otp_sent'">
          <b-form-group>
            <label class="required-label">OTP:</label>
            <b-form-input v-model="otp" type="text" placeholder="Enter OTP" />
          </b-form-group>
          <b-row class="justify-content-center">
            <b-button :disabled="!otp" class="btn-success shadow-sm" @click="loginUser">
              Login
            </b-button>
          </b-row>
        </div>

      </b-col>
    </div>
  </div>
</template>

<script>
import PublicAdmissionForm from "./PublicAdmissionForm.vue";

export default {
  components: { PublicAdmissionForm },
  data() {
    return {
      errorMsg: '',
      email: this.$route.params.email || "",
      name: null,
      otp: this.$route.params.otp || "",
      otpResendCountdown: 0,
      loginStatus: "",
      admissionFormDetails: {},
      admission_form_id: null,
      isApplicationFormVisible: false,
    };
  },
  created() {
    this.fetchAdmissionFormDetails();
  },
  watch: {
    '$route.params.admissionform_id'(newId) {
      this.admission_form_id = newId;
      this.fetchAdmissionFormDetails();
    }
  },
  methods: {
    closeTab() {
      window.close();
    },

    /** Logout the user and reset stored data */
    logout() {
      localStorage.removeItem("admissionformUserAuthToken");
      localStorage.removeItem("admissionformUserAuthEmail");
      this.isApplicationFormVisible = false;
      this.email = "";
      this.name = null;
      this.otp = "";
      this.otpResendCountdown = 0;
      this.loginStatus = "";
    },

    /** Fetch admission form details based on public ID */
    async fetchAdmissionFormDetails() {
      this.admission_form_id = this.$route.params.admissionform_id;
      const url = `${this.$store.getters.getAPIKey.mainAPI}/admissionforms/details/${this.admission_form_id}`;
      try {
        const response = await this.$axios.get(url);
        this.admissionFormDetails = response.data;
      } catch (error) {
        console.error("Error fetching form details:", error);
      }
    },

    /** Email validation */
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    /** Handle user login with OTP */
    async loginUser() {
      const url = `${this.$store.getters.getAPIKey.mainAPI}/admissionforms/login/with_otp`;

      if (!this.isValidEmail(this.email)) {
        this.$toast.error("Please enter a valid email address.", {
          position: "top",
          duration: 4000,
        });
        return;
      }

      try {
        const response = await this.$axios.post(url, {
          email: this.email,
          name: this.name,
          otp: this.otp,
          admission_form_id: this.admissionFormDetails.id
        });

        if (response.data) {
          this.isApplicationFormVisible = true;
        }
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Failed to login. Try again.", {
          position: "top",
          duration: 7000,
        });
      }
    },

    /** Start OTP resend countdown */
    startOtpResendCountdown() {
      if (this.otpResendCountdown > 0) return;
      this.otpResendCountdown = 120;
      const countdownInterval = setInterval(() => {
        this.otpResendCountdown--;
        if (this.otpResendCountdown <= 0) clearInterval(countdownInterval);
      }, 1000);
    },

    /** Request OTP for login */
    async sendOtp() {
      if (!this.isValidEmail(this.email)) {
        this.$toast.error("Please enter a valid email address.", {
          position: "top",
          duration: 4000,
        });
        return;
      }

      this.errorMsg = '';
      this.loginStatus = "sending";
      const url = `${this.$store.getters.getAPIKey.mainAPI}/admissionforms/login/request_otp`;

      try {
        const response = await this.$axios.post(url, {
          email: this.email,
          name: this.name,
          admission_form_id: this.admission_form_id,
        });

        if (response.data) {
          this.name = response.data;
        }

        this.$toast.success("OTP sent successfully!", {
          position: "top",
          duration: 4000,
        });

        this.startOtpResendCountdown();
        this.loginStatus = "otp_sent";
      } catch (error) {
        if (error.response?.data?.message) {
          this.errorMsg = error.response.data.message;
        }
        this.loginStatus = "";
      }
    },

    /** Handle form submission */
    handleFormSubmission() {
      this.isApplicationFormVisible = false;
    }
  },
};
</script>

<style scoped>
.container-fluid {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px;
  background: linear-gradient(90deg, rgba(150, 219, 255, 0.58) 0%, rgba(222, 136, 255, 0.54) 100%);
}

.login-card {
  width: 390px;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
}
</style>
